// src/components/Eventos/EventsHeats/components/FieldAttemptCard.jsx
import React from 'react';
import { Box, Typography, Card, Chip, useTheme, useMediaQuery } from '@mui/material';
import { getFontSize, isSpecial } from '../utils/eventHelpers';

const FieldAttemptCard = ({ 
  attemptNumber, 
  result, 
  position, 
  positionChange,
  wind,
  isBestAttempt = false,
  isValidAttempt = true 
}) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isSm = useMediaQuery(theme.breakpoints.down('md'));
  const isMd = useMediaQuery(theme.breakpoints.down('lg'));

  const getPositionChangeDisplay = () => {
    if (!positionChange || positionChange.val === 0) {
      return {
        text: '0',
        color: theme.palette.text.secondary
      };
    }
    
    if (positionChange.dir === 'up') {
      return {
        text: `+${positionChange.val}`,
        color: theme.palette.success.main
      };
    } else {
      return {
        text: `-${positionChange.val}`,
        color: theme.palette.error.main
      };
    }
  };

  const getBorderColor = () => {
    if (isBestAttempt) return theme.palette.success.main;
    // NUEVO: borde rojo para fauls
    if (result === 'X') return theme.palette.error.main;
    return '#e0e0e0';
  };

  const getResultChipProps = () => {
    // NUEVO: manejo específico para fauls
    if (result === 'X') {
      return {
        variant: 'filled',
        sx: {
          backgroundColor: '#f44336', // Rojo para fauls
          color: 'white',
          fontWeight: 'bold',
          border: '2px solid #d32f2f'
        }
      };
    }
    
    if (isSpecial(result)) {
      return {
        variant: 'filled',
        sx: {
          backgroundColor: theme.palette.error.main,
          color: 'white',
          fontWeight: 'bold'
        }
      };
    }
    
    if (isBestAttempt) {
      return {
        variant: 'filled',
        sx: {
          backgroundColor: theme.palette.success.main,
          color: 'white',
          fontWeight: 'bold'
        }
      };
    }
    
    return {
      variant: 'outlined',
      sx: {
        borderColor: theme.palette.primary.main,
        color: theme.palette.primary.main,
        fontWeight: 'bold'
      }
    };
  };

  const positionChangeDisplay = getPositionChangeDisplay();

  return (
    <Card
      sx={{
        position: 'relative',
        minHeight: isXs ? 90 : 110,
        backgroundColor: '#ffffff',
        border: `2px solid ${getBorderColor()}`,
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[4],
        }
      }}
    >
      {/* Sección superior - Número de intento */}
      <Box
        sx={{
          backgroundColor: isBestAttempt 
            ? theme.palette.success.main 
            : result === 'X' 
              ? theme.palette.error.main // NUEVO: fondo rojo para fauls
              : '#f5f5f5', // Solo el mejor intento en verde, fauls en rojo, todos los demás en gris
          color: (isBestAttempt || result === 'X') ? 'white' : theme.palette.text.primary,
          py: 1,
          px: 2,
          textAlign: 'center',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography
          sx={{
            fontSize: getFontSize(14, 1, isXs, isSm, isMd),
            fontWeight: 'bold',
            lineHeight: 1
          }}
        >
          Intento {attemptNumber}
        </Typography>
      </Box>

      {/* Posición en esquina superior izquierda - estilo más parecido a pista */}
      <Box
        sx={{
          position: 'absolute',
          top: isXs ? 45 : 50,
          left: 8,
          zIndex: 2
        }}
      >
        <Typography
          sx={{
            fontSize: getFontSize(14, 1, isXs, isSm, isMd),
            fontWeight: 'bold',
            color: theme.palette.primary.main, // Color azul para el texto
            backgroundColor: 'transparent', // Fondo transparente
            px: 0.5,
            py: 0.2,
            borderRadius: 0.5,
            minWidth: 20,
            textAlign: 'center',
            border: `1px solid ${theme.palette.primary.main}` // Borde azul sutil
          }}
        >
          {position || '-'}
        </Typography>
      </Box>

      {/* Cambio de posición en esquina superior derecha */}
      <Box
        sx={{
          position: 'absolute',
          top: isXs ? 45 : 50,
          right: 8,
          zIndex: 2
        }}
      >
        <Typography
          sx={{
            fontSize: getFontSize(12, 1, isXs, isSm, isMd),
            fontWeight: 'bold',
            color: positionChangeDisplay.color, // Color según dirección
            backgroundColor: 'transparent', // Fondo transparente
            px: 0.5,
            py: 0.2,
            borderRadius: 0.5,
            minWidth: 20,
            textAlign: 'center',
            border: `1px solid ${positionChangeDisplay.color}` // Borde del color correspondiente
          }}
        >
          {positionChangeDisplay.text}
        </Typography>
      </Box>

      {/* Sección inferior - Datos */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: isXs ? 58 : 68,
          px: 2,
          flexDirection: 'column'
        }}
      >
        {/* Resultado en chip */}
        <Chip
          label={result || '-'}
          size="small"
          {...getResultChipProps()}
          sx={{
            ...getResultChipProps().sx,
            fontSize: getFontSize(14, 1, isXs, isSm, isMd),
            height: isXs ? 24 : 28,
            mb: wind && wind.trim() ? 0.5 : 0,
            minWidth: isXs ? 80 : 100
          }}
        />

        {/* Viento (si aplica) */}
        {wind && wind.trim() && (
          <Typography
            sx={{
              fontSize: getFontSize(11, 1, isXs, isSm, isMd),
              color: theme.palette.text.secondary,
              lineHeight: 1,
              textAlign: 'center'
            }}
          >
            {wind} m/s
          </Typography>
        )}
      </Box>
    </Card>
  );
};

export default FieldAttemptCard;